import React, { useCallback, useState, useEffect } from "react";
import ReactDataGrid from "react-data-grid";
import { useField } from "react-final-form";
import { findIndex } from "lodash";
import { useNotify } from "react-admin";
import { isNumber } from "utils/tools";

interface IAreaDatagrid {
  edit?: boolean;
  areas: ISubcontractorAreas[];
  isDisabled?: boolean;
}

interface ISchoolIncentive {
  subcontractorAreaId: number;
  area: string;
  feeVehicleSizeSmall: number;
  feeVehicleSizeMedium: number;
  feeVehicleSizeLarge: number;
}

interface IRateChartInsert {
  subcontractorAreaId: number;
  vehicleSize: string;
  fee: number;
}

const PriceFormatter = ({ value }: any) => {
  return (
    <span>
      {Number(Math.round((Number(value) + Number.EPSILON) * 100) / 100)}
    </span>
  );
};

const columns = [
  { key: "area", name: "Area" },
  {
    key: "feeVehicleSizeSmall",
    name: "Vehicle Size Small",
    editable: true,
    formatter: PriceFormatter,
  },
  {
    key: "feeVehicleSizeMedium",
    name: "Vehicle Size Medium",
    editable: true,
    formatter: PriceFormatter,
  },
  {
    key: "feeVehicleSizeLarge",
    name: "Vehicle Size Large",
    editable: true,
    formatter: PriceFormatter,
  },
];

const SubcontractorRateChartAreaDatagridSchoolIncentive = ({
  areas,
  edit,
  isDisabled,
}: IAreaDatagrid) => {
  const [schoolIncentives, setSchoolIncentives] = useState<ISchoolIncentive[] | []>([]);
  const [isLoaded, setLoaded] = useState(false);

  const notify = useNotify();

  const subcontractorRateChartSchoolIncentives = useField("schoolIncentives");
  const schoolIncentivesToSend = useField("schoolIncentivesToSend");

  //populate schoolIncentives data with updated areas data from parent component
  useEffect(() => {
    let rowData: ISchoolIncentive[] = [];

    if (!edit) {
      //prefill empty placeholder for create-case
      areas.forEach(({ order, id, maxDistanceInMeters }: any) => {
        const data: ISchoolIncentive = {
          area: `Area ${order} (${maxDistanceInMeters}m)`,
          subcontractorAreaId: id,
          feeVehicleSizeLarge: 0,
          feeVehicleSizeMedium: 0,
          feeVehicleSizeSmall: 0,
        };
        rowData.push(data);
      });
    }
    setSchoolIncentives(rowData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areas]);

  useEffect(() => {
    if (areas.length !== 0) {
      if (!isLoaded && subcontractorRateChartSchoolIncentives.input.value !== "") {
        let rowData: ISchoolIncentive[] = [];

        areas.forEach(({ order, id, maxDistanceInMeters }: any) => {
          const data: ISchoolIncentive = {
            area: `Area ${order} (${maxDistanceInMeters}m)`,
            subcontractorAreaId: id,
            feeVehicleSizeLarge: 0,
            feeVehicleSizeMedium: 0,
            feeVehicleSizeSmall: 0,
          };
          rowData.push(data);
        });

        subcontractorRateChartSchoolIncentives.input.value.forEach((item: any) => {
          let area = findIndex(rowData, {
            subcontractorAreaId: item.subcontractorAreaId,
          });

          if (item.vehicleSize === "small") {
            rowData[area].feeVehicleSizeSmall = item.fee / 100;
          }

          if (item.vehicleSize === "medium") {
            rowData[area].feeVehicleSizeMedium = item.fee / 100;
          }

          if (item.vehicleSize === "large") {
            rowData[area].feeVehicleSizeLarge = item.fee / 100;
          }
        });

        setSchoolIncentives(rowData);
        setLoaded(true);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subcontractorRateChartSchoolIncentives.input.value, areas]);

  //update react-form data provider if there is a change in schoolIncentives
  useEffect(() => {
    const insertData: IRateChartInsert[] = [];

    schoolIncentives.forEach((item: ISchoolIncentive) => {
      insertData.push({
        subcontractorAreaId: item.subcontractorAreaId,
        vehicleSize: "small",
        fee: Number(item.feeVehicleSizeSmall) * 100,
      });
      insertData.push({
        subcontractorAreaId: item.subcontractorAreaId,
        vehicleSize: "medium",
        fee: Number(item.feeVehicleSizeMedium) * 100,
      });
      insertData.push({
        subcontractorAreaId: item.subcontractorAreaId,
        vehicleSize: "large",
        fee: Number(item.feeVehicleSizeLarge) * 100,
      });
    });

    schoolIncentivesToSend.input.onChange(insertData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolIncentives]);

  const onGridRowsUpdated = useCallback(
    ({
      fromRow,
      toRow,
      updated,
    }: ReactDataGrid.GridRowsUpdatedEvent<ISchoolIncentive>) => {
      const updatedRows = schoolIncentives.slice();

      if (
        (updated.hasOwnProperty("feeVehicleSizeSmall") &&
          !isNumber(Number(updated.feeVehicleSizeSmall))) ||
        (updated.hasOwnProperty("feeVehicleSizeMedium") &&
          !isNumber(Number(updated.feeVehicleSizeMedium))) ||
        (updated.hasOwnProperty("feeVehicleSizeLarge") &&
          !isNumber(Number(updated.feeVehicleSizeLarge)))
      ) {
        return notify(`Please input Number`, "warning");
      }

      for (let i = fromRow; i <= toRow; i++) {
        updatedRows[i] = { ...updatedRows[i], ...updated };
      }

      setSchoolIncentives(updatedRows);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setSchoolIncentives, schoolIncentives]
  );

  if (areas.length === 0) {
    return <h4>Subcontractor Area data is not set.</h4>;
  }

  if (schoolIncentives.length === 0) {
    return <h4>Loading...</h4>;
  } else {
    return (
      <ReactDataGrid
        columns={columns}
        rowGetter={(i) => schoolIncentives[i]}
        rowsCount={schoolIncentives.length}
        enableCellSelect={isDisabled ? false : true}
        onGridRowsUpdated={onGridRowsUpdated}
        minWidth={700}
      />
    );
  }
};
export default SubcontractorRateChartAreaDatagridSchoolIncentive;
